import { useEffect, useState } from "react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Users, CreditCard, Package, DollarSign, Ban, CheckCircle2 } from "lucide-react";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-white border border-[#e8d8c8] p-5 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#f5ebe1] flex items-center justify-center text-[#9c5f27]"><Icon size={20} /></div>
        <div>
          <div className="text-2xl font-bold font-display text-[#2e1e10]">{value}</div>
          <div className="text-xs text-[#8c735f]">{label}</div>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [subs, setSubs] = useState([]);

  const load = async () => {
    const [s, u, sb] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/users"), api.get("/admin/subscriptions"),
    ]);
    setStats(s.data); setUsers(u.data.users); setSubs(sb.data.subscriptions);
  };

  useEffect(() => { load(); }, []);

  const toggle = async (u) => {
    const next = u.subscription_status === "active" ? "canceled" : "active";
    try {
      await api.post(`/admin/users/${u.id}/subscription`, { status: next });
      toast.success(next === "active" ? "Acesso reativado" : "Acesso bloqueado (dados preservados)");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen mesh-warm">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-panel-container">
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#9c5f27]">Painel administrativo</h1>
        <p className="mt-2 text-[#5c4634]">Usuários, assinaturas e uso da plataforma.</p>

        {stats && (
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Stat icon={Users} label="Vendedores" value={stats.total_users} />
            <Stat icon={CheckCircle2} label="Assinaturas ativas" value={stats.active_subscriptions} />
            <Stat icon={Ban} label="Canceladas/inativas" value={stats.canceled_subscriptions} />
            <Stat icon={Package} label="Anúncios prontos" value={stats.ready_products} />
            <Stat icon={DollarSign} label="MRR (R$)" value={stats.mrr.toFixed(2).replace(".", ",")} />
          </div>
        )}

        <Tabs defaultValue="users" className="mt-8">
          <TabsList className="bg-[#f5ebe1]">
            <TabsTrigger value="users" data-testid="tab-users">Usuários</TabsTrigger>
            <TabsTrigger value="subs" data-testid="tab-subscriptions">Assinaturas</TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-4">
            <div className="rounded-2xl bg-white border border-[#e8d8c8] overflow-hidden shadow-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#fdf8f3] text-[#8c735f] text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Vendedor</th>
                    <th className="px-4 py-3 font-semibold">Plano</th>
                    <th className="px-4 py-3 font-semibold">Anúncios</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold text-right">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-[#f0e5d8]" data-testid={`admin-user-row-${u.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-[#2e1e10]">{u.name}</div>
                        <div className="text-xs text-[#8c735f]">{u.email}</div>
                      </td>
                      <td className="px-4 py-3 text-[#5c4634]">{u.plan_id || "—"}</td>
                      <td className="px-4 py-3 text-[#5c4634]">{u.products_count}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${u.subscription_status === "active" ? "bg-[#dcfce7] text-[#15803d]" : "bg-[#fee2e2] text-[#b91c1c]"}`}>
                          {u.subscription_status === "active" ? "Ativa" : "Inativa"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => toggle(u)} data-testid={`toggle-sub-${u.id}`}
                          className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${u.subscription_status === "active" ? "border-[#b91c1c] text-[#b91c1c] hover:bg-[#fee2e2]" : "border-[#15803d] text-[#15803d] hover:bg-[#dcfce7]"}`}>
                          {u.subscription_status === "active" ? "Bloquear" : "Reativar"}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#8c735f]">Nenhum vendedor ainda.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="subs" className="mt-4">
            <div className="rounded-2xl bg-white border border-[#e8d8c8] overflow-hidden shadow-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#fdf8f3] text-[#8c735f] text-left">
                  <tr>
                    <th className="px-4 py-3 font-semibold">E-mail</th>
                    <th className="px-4 py-3 font-semibold">Plano</th>
                    <th className="px-4 py-3 font-semibold">Valor</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Início</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s, i) => (
                    <tr key={`${s.user_email}-${i}`} className="border-t border-[#f0e5d8]">
                      <td className="px-4 py-3 text-[#2e1e10]">{s.user_email}</td>
                      <td className="px-4 py-3 text-[#5c4634]">{s.plan_id}</td>
                      <td className="px-4 py-3 text-[#5c4634]">R$ {Number(s.amount).toFixed(2).replace(".", ",")}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${s.status === "active" ? "bg-[#dcfce7] text-[#15803d]" : "bg-[#fee2e2] text-[#b91c1c]"}`}>{s.status}</span>
                      </td>
                      <td className="px-4 py-3 text-[#8c735f]">{s.started_at ? new Date(s.started_at).toLocaleDateString("pt-BR") : "—"}</td>
                    </tr>
                  ))}
                  {subs.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#8c735f]">Nenhuma assinatura ainda.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
